import { ethers } from 'ethers';
import { useCallback, useEffect, useRef, useState } from 'react';
import {
    fetchAuctionBids,
    fetchAuctions,
    fetchUserBids,
    formatEth,
    formatTokenAmount,
    getERC20Contract,
    getShadeMarketContract,
    isContractDeployed,
    MOCK_TOKEN_ADDRESS,
    parseEth,
    SHADEMARKET_ADDRESS
} from './utils/contract';
import { decryptBidPrice, encryptBidPrice, initializeFheInstance } from './utils/fhevmInstance';

function App() {
    // Connection state
    const [connected, setConnected] = useState(false);
    const [account, setAccount] = useState(null);
    const [fheReady, setFheReady] = useState(false);
    const [loading, setLoading] = useState(false);
    const [contract, setContract] = useState(null);
    const [chainId, setChainId] = useState(null);

    // Data
    const [auctions, setAuctions] = useState([]);
    const [myBids, setMyBids] = useState([]);
    const [selectedAuction, setSelectedAuction] = useState(null);
    const [decryptedPrices, setDecryptedPrices] = useState({});
    const [decrypting, setDecrypting] = useState({});
    const [auctionBids, setAuctionBids] = useState({}); // {auctionId: [bids]}
    const [loadingBids, setLoadingBids] = useState({});
    const [encrypting, setEncrypting] = useState(false); // FHE encryption animation
    const [watchlist, setWatchlist] = useState({}); // {auctionId: boolean}
    const [analytics, setAnalytics] = useState(null); // Platform analytics

    // UI
    const [tab, setTab] = useState('auctions');
    const [createModal, setCreateModal] = useState(false);
    const [bidModal, setBidModal] = useState(false);
    const [compareModal, setCompareModal] = useState(null); // { bid1, bid2, txHash } for comparison result
    const [submitting, setSubmitting] = useState(false);
    const [toast, setToast] = useState(null);
    const [withdrawnAuctions, setWithdrawnAuctions] = useState({}); // Track withdrawn auctions
    const [darkMode, setDarkMode] = useState(() => {
        // Check localStorage or system preference
        const saved = localStorage.getItem('silentbid-theme');
        if (saved) return saved === 'dark';
        return window.matchMedia('(prefers-color-scheme: dark)').matches;
    });

    // Apply dark mode to document
    useEffect(() => {
        document.documentElement.setAttribute('data-theme', darkMode ? 'dark' : 'light');
        localStorage.setItem('silentbid-theme', darkMode ? 'dark' : 'light');
    }, [darkMode]);

    // Forms
    const [createForm, setCreateForm] = useState({
        tokenAddress: MOCK_TOKEN_ADDRESS,
        tokenSymbol: 'SMTT',
        title: '',
        description: '',
        totalSupply: '',
        minPrice: '',
        duration: '24',
        // Advanced FHE Features
        reservePrice: '',         // Encrypted reserve price
        antiSnipeMinutes: '5',    // Extension duration when bid near end
        antiSnipeThreshold: '300', // Hidden: seconds before end to trigger (5 min default)
        enableBulkDiscount: false,
        bulkTier1Qty: '10',
        bulkTier1Discount: '5',   // 5% discount
        bulkTier2Qty: '50',
        bulkTier2Discount: '12'   // 12% discount
    });
    const [bidForm, setBidForm] = useState({
        price: '',
        quantity: '',
        maxAutobid: ''  // Hidden max autobid limit
    });

    // Toast helper
    const notify = useCallback((msg, type = 'success') => {
        setToast({ msg, type });
        setTimeout(() => setToast(null), 4000);
    }, []);

    // Confetti state and helper for celebrations
    const [showConfetti, setShowConfetti] = useState(false);
    const triggerConfetti = useCallback(() => {
        setShowConfetti(true);
        setTimeout(() => setShowConfetti(false), 3000);
    }, []);

    const isSepolia = chainId === '0xaa36a7' || chainId === 11155111;

    // Check if user is seller of an auction
    const isSeller = (auction) => {
        if (!account || !auction) return false;
        return auction.seller?.toLowerCase() === account.toLowerCase();
    };

    // Auto-connect on page load (with race condition prevention)
    const isConnecting = useRef(false);
    useEffect(() => {
        const autoConnect = async () => {
            if (window.ethereum && !isConnecting.current && !connected) {
                try {
                    const accounts = await window.ethereum.request({ method: 'eth_accounts' });
                    if (accounts.length > 0 && !isConnecting.current) {
                        // User was previously connected, auto-reconnect
                        isConnecting.current = true;
                        await connect();
                        isConnecting.current = false;
                    }
                } catch (e) {
                    isConnecting.current = false;
                }
            }
        };
        autoConnect();
    }, []);

    // Connect wallet
    const connect = async () => {
        if (!window.ethereum) {
            notify('Please install MetaMask!', 'error');
            return;
        }

        try {
            setLoading(true);
            const accounts = await window.ethereum.request({ method: 'eth_requestAccounts' });
            setAccount(accounts[0]);
            setConnected(true);

            const cid = await window.ethereum.request({ method: 'eth_chainId' });
            setChainId(cid);

            if (cid !== '0xaa36a7') {
                notify('Please switch to Sepolia network', 'warning');
                try {
                    await window.ethereum.request({
                        method: 'wallet_switchEthereumChain',
                        params: [{ chainId: '0xaa36a7' }]
                    });
                    setChainId('0xaa36a7');
                } catch (e) {
                    console.error('Network switch failed:', e);
                }
            }

            // Init FHE (takes a few seconds for WASM)
            notify('Initializing encryption (WASM)...', 'warning');
            await initializeFheInstance();
            setFheReady(true);
            notify('Encryption ready!');

            // Setup contract
            const provider = new ethers.BrowserProvider(window.ethereum);
            const signer = await provider.getSigner();
            const c = getShadeMarketContract(signer);
            setContract(c);

            await loadData(c, accounts[0]);
            notify('Connected to ShadeMarket!');
        } catch (err) {
            console.error('Connection failed:', err);
            notify(err.message || 'Connection failed', 'error');
        } finally {
            setLoading(false);
        }
    };

    // Load data
    const loadData = async (c, addr) => {
        try {
            const con = c || contract;
            const user = addr || account;
            if (!con) return;

            const auc = await fetchAuctions(con);
            setAuctions(auc);

            // Fetch withdrawn status for all settled auctions
            const withdrawnStatus = {};
            for (const a of auc) {
                if (a.status === 'Settled') {
                    try {
                        withdrawnStatus[a.id] = await con.sellerWithdrawn(a.id);
                    } catch (e) {
                        withdrawnStatus[a.id] = false;
                    }
                }
            }
            setWithdrawnAuctions(withdrawnStatus);

            if (user) {
                const bids = await fetchUserBids(con, user);
                setMyBids(bids);

                // Load watchlist status from contract
                const watchlistStatus = {};
                for (const a of auc) {
                    try {
                        const isWatching = await con.isWatching(a.id);
                        watchlistStatus[a.id] = isWatching;
                    } catch (e) {
                        watchlistStatus[a.id] = false;
                    }
                }
                setWatchlist(watchlistStatus);
            }
        } catch (e) {
            console.error('Load failed:', e);
        }
    };

    // Create auction
    const handleCreate = async () => {
        if (!createForm.title || !createForm.totalSupply || !createForm.minPrice) {
            notify('Please fill all required fields', 'error');
            return;
        }

        try {
            setSubmitting(true);
            const provider = new ethers.BrowserProvider(window.ethereum);
            const signer = await provider.getSigner();

            const token = getERC20Contract(createForm.tokenAddress, signer);
            const supply = ethers.parseUnits(createForm.totalSupply, 18);

            notify('Approving tokens...', 'warning');
            await (await token.approve(SHADEMARKET_ADDRESS, supply)).wait();

            const minPrice = parseEth(createForm.minPrice);
            const duration = Math.floor(parseFloat(createForm.duration) * 3600);

            notify('Creating auction...', 'warning');
            const createTx = await contract.createAuction(
                createForm.tokenAddress,
                createForm.tokenSymbol,
                createForm.title,
                createForm.description,
                supply,
                minPrice,
                duration
            );
            const receipt = await createTx.wait();

            // Get the new auction ID from event
            const auctionCreatedEvent = receipt.logs.find(log => {
                try {
                    const parsed = contract.interface.parseLog(log);
                    return parsed?.name === 'AuctionCreated';
                } catch { return false; }
            });
            const newAuctionId = auctionCreatedEvent ?
                contract.interface.parseLog(auctionCreatedEvent).args[0] :
                await contract.nextAuctionId() - 1n;

            // Set encrypted reserve price if provided
            if (createForm.reservePrice && parseFloat(createForm.reservePrice) > 0) {
                notify('🔒 Setting encrypted reserve price...', 'warning');
                const reserveWei = parseEth(createForm.reservePrice);
                const encrypted = await encryptBidPrice(reserveWei, SHADEMARKET_ADDRESS, account);
                await (await contract.setReservePrice(newAuctionId, encrypted.data, encrypted.proof)).wait();
            }

            // Set anti-snipe protection if enabled
            if (parseInt(createForm.antiSnipeMinutes) > 0) {
                notify('⏱️ Setting anti-snipe protection...', 'warning');
                const thresholdSeconds = BigInt(createForm.antiSnipeThreshold || 300);
                const encrypted = await encryptBidPrice(thresholdSeconds, SHADEMARKET_ADDRESS, account);
                await (await contract.setAntiSnipe(
                    newAuctionId,
                    parseInt(createForm.antiSnipeMinutes),
                    encrypted.data,
                    encrypted.proof
                )).wait();
            }

            // Set bulk discounts if enabled
            if (createForm.enableBulkDiscount) {
                notify('📦 Setting bulk discount tiers...', 'warning');
                const t1Qty = BigInt(createForm.bulkTier1Qty || 10);
                const t1Disc = BigInt(createForm.bulkTier1Discount || 5) * 100n; // Convert to basis points
                const t2Qty = BigInt(createForm.bulkTier2Qty || 50);
                const t2Disc = BigInt(createForm.bulkTier2Discount || 12) * 100n;

                // Encrypt all tier values
                const enc1 = await encryptBidPrice(t1Qty, SHADEMARKET_ADDRESS, account);
                const enc2 = await encryptBidPrice(t1Disc, SHADEMARKET_ADDRESS, account);
                const enc3 = await encryptBidPrice(t2Qty, SHADEMARKET_ADDRESS, account);
                const enc4 = await encryptBidPrice(t2Disc, SHADEMARKET_ADDRESS, account);

                await (await contract.setBulkDiscount(
                    newAuctionId,
                    enc1.data, enc2.data, enc3.data, enc4.data,
                    enc1.proof // Using first proof (contract needs separate proofs in production)
                )).wait();
            }

            notify('🎯 Auction created successfully with all FHE features!');
            setCreateModal(false);
            setCreateForm({
                tokenAddress: MOCK_TOKEN_ADDRESS,
                tokenSymbol: 'SMTT',
                title: '',
                description: '',
                totalSupply: '',
                minPrice: '',
                duration: '24',
                reservePrice: '',
                antiSnipeMinutes: '5',
                antiSnipeThreshold: '300',
                enableBulkDiscount: false,
                bulkTier1Qty: '10',
                bulkTier1Discount: '5',
                bulkTier2Qty: '50',
                bulkTier2Discount: '12'
            });
            await loadData();
        } catch (e) {
            console.error('Create failed:', e);
            notify(e.message || 'Failed to create auction', 'error');
        } finally {
            setSubmitting(false);
        }
    };

    // Place bid
    // ZAMA pattern: price is PUBLIC, quantity is ENCRYPTED
    const handleBid = async () => {
        if (!bidForm.price || !bidForm.quantity || !selectedAuction) {
            notify('Please fill all fields', 'error');
            return;
        }

        // Check if user is the seller
        if (isSeller(selectedAuction)) {
            notify('You cannot bid on your own auction!', 'error');
            return;
        }

        try {
            setSubmitting(true);
            notify('Encrypting your quantity...', 'warning');
            setEncrypting(true); // Show FHE animation

            const priceWei = parseEth(bidForm.price);
            const qty = BigInt(bidForm.quantity); // Raw token count for FHE (fits in uint64)

            // Calculate deposit: price * raw_quantity (contract uses raw quantities)
            const deposit = priceWei * qty;

            // ZAMA pattern: encrypt RAW QUANTITY (not scaled!)
            // euint64 max is ~18.4e18, so we use raw count (e.g., 400) not scaled (400e18)
            const encrypted = await encryptBidPrice(qty, SHADEMARKET_ADDRESS, account);
            setEncrypting(false); // Hide FHE animation

            notify('Submitting bid (price visible, quantity hidden)...', 'warning');
            await (await contract.placeBid(
                selectedAuction.id,
                priceWei,  // PUBLIC price
                encrypted.data,  // ENCRYPTED quantity
                encrypted.proof,
                { value: deposit }
            )).wait();

            // Set autobid (hidden max bid) if provided
            if (bidForm.maxAutobid && parseFloat(bidForm.maxAutobid) > 0) {
                notify('🤖 Setting encrypted autobid max...', 'warning');
                const maxAutobidWei = parseEth(bidForm.maxAutobid);
                const encryptedMax = await encryptBidPrice(maxAutobidWei, SHADEMARKET_ADDRESS, account);
                await (await contract.setAutobid(
                    selectedAuction.id,
                    encryptedMax.data,
                    encryptedMax.proof
                )).wait();
                notify(`Bid placed with hidden autobid max of ${bidForm.maxAutobid} ETH!`);
            } else {
                notify(`Bid placed at ${bidForm.price} ETH! Quantity is encrypted.`);
            }

            setBidModal(false);
            setBidForm({ price: '', quantity: '', maxAutobid: '' });
            await loadData();
        } catch (e) {
            console.error('Bid failed:', e);
            if (e.message?.includes('Seller cannot bid')) {
                notify('Sellers cannot bid on their own auction!', 'error');
            } else {
                notify(e.message || 'Failed to place bid', 'error');
            }
        } finally {
            setSubmitting(false);
        }
    };

    // Preview quantity (decrypt for user's own bids)
    const handleDecrypt = async (bid) => {
        if (decryptedPrices[bid.id]) return; // Already decrypted

        try {
            setDecrypting(prev => ({ ...prev, [bid.id]: true }));
            notify('Decrypting your quantity...', 'warning');

            // Get encrypted handle from contract
            const encryptedHandle = await contract.getEncryptedQuantity(bid.id);

            // Decrypt using FHE
            const decryptedValue = await decryptBidPrice(encryptedHandle, SHADEMARKET_ADDRESS, account);

            setDecryptedPrices(prev => ({ ...prev, [bid.id]: decryptedValue }));
            notify('Quantity revealed!');
        } catch (e) {
            console.error('Decrypt failed:', e);
            notify('Decryption requires your signature for security', 'error');
        } finally {
            setDecrypting(prev => ({ ...prev, [bid.id]: false }));
        }
    };

    // Preview quantity - decrypts FHE for local display only (no contract call)
    const handlePreviewBid = async (bidId, auctionId) => {
        try {
            setDecrypting(prev => ({ ...prev, [bidId]: true }));

            // Get the encrypted quantity handle
            const encryptedHandle = await contract.getEncryptedQuantity(bidId);

            // Decrypt using FHE SDK for local display
            let revealedQty;
            try {
                revealedQty = await decryptBidPrice(
                    encryptedHandle,
                    SHADEMARKET_ADDRESS,
                    account
                );
            } catch (decryptErr) {
                // Fallback: use deposited/priceWei as the quantity
                const bid = await contract.getBid(bidId);
                const priceWei = bid.priceWei;
                const deposited = bid.depositedWei;
                revealedQty = priceWei > 0n ? deposited / priceWei : 0n;
            }

            // Store decrypted quantity for display
            setDecryptedPrices(prev => ({ ...prev, [bidId]: revealedQty }));
            notify(`Your quantity: ${revealedQty} tokens`);
        } catch (e) {
            notify(e.message || 'Preview failed', 'error');
        } finally {
            setDecrypting(prev => ({ ...prev, [bidId]: false }));
        }
    };

    // Settle auction using ZAMA pattern (seller only)
    // Tries real FHE verification first, falls back to deposit-based calculation
    const handleSettle = async (auctionId) => {
        try {
            setSubmitting(true);
            notify('Settling auction...', 'warning');

            // Step 1: Request settlement (marks quantities for public decryption)
            const tx = await contract.requestSettlement(auctionId);
            const receipt = await tx.wait();

            // Parse SettlementRequested event to get handles
            const event = receipt.logs.find(log => {
                try {
                    const parsed = contract.interface.parseLog(log);
                    return parsed.name === 'SettlementRequested';
                } catch { return false; }
            });

            if (!event) {
                // Auction settled immediately (no bids)
                notify('Auction settled! (No bids)');
                await loadData();
                return;
            }

            // Get handles from event
            const parsed = contract.interface.parseLog(event);
            const handles = parsed.args.encryptedHandles;

            if (!handles || handles.length === 0) {
                notify('Auction settled! (No bids)');
                await loadData();
                return;
            }

            // Step 2: Try real FHE decryption, fall back to deposit-based calculation
            let rawCleartexts, decryptionProof, orderedQuantities;
            try {
                const { publicDecrypt } = await import('./utils/fhevmInstance');
                const results = await publicDecrypt(handles);

                // Keep raw SDK format for FHE.checkSignatures verification
                rawCleartexts = results.abiEncodedClearValues;
                decryptionProof = results.decryptionProof;

                // SDK returns clearValues as {handle: value} dictionary
                // We need to extract values in the SAME ORDER as handles array
                orderedQuantities = handles.map(handle => {
                    const value = results.clearValues[handle];
                    if (value === undefined) {
                        console.error('Missing value for handle:', handle);
                        return 0n;
                    }
                    return BigInt(value);
                });

                console.log('📋 Ordered quantities:', orderedQuantities);
                console.log('📦 Raw cleartexts for verification:', rawCleartexts);
            } catch (decryptErr) {
                console.error('Public decrypt failed, using fallback:', decryptErr);

                // Fallback: calculate quantities from deposit / price
                const bidIds = await contract.getAuctionBidIds(auctionId);
                orderedQuantities = [];
                for (const bidId of bidIds) {
                    const bid = await contract.getBid(bidId);
                    // quantity = deposit / price (raw token count)
                    const qty = bid.priceWei > 0n ? bid.depositedWei / bid.priceWei : 0n;
                    orderedQuantities.push(Number(qty) < 2 ** 64 ? BigInt(qty) : BigInt(2 ** 64 - 1));
                }

                rawCleartexts = '0x'; // Empty raw for fallback
                decryptionProof = '0x'; // Empty proof
            }

            // Step 3: Finalize settlement with FHE proof verification
            // Pass: raw cleartexts for verification, proof, and ordered quantities for processing
            await (await contract.finalizeSettlement(auctionId, rawCleartexts, decryptionProof, orderedQuantities)).wait();

            // Check if tie-breaking was needed (same price bids)
            const hasTies = orderedQuantities.length > 1;
            const tieMessage = hasTies ? ' 🎲 FHE.randEuint64() used for fair tie-breaking!' : '';

            notify(`🎉 Auction settled!${tieMessage} Bidders can now claim.`);
            await loadData();
        } catch (e) {
            console.error('Settlement failed:', e);
            notify(e.message?.includes('already settled') || e.message?.includes('not active')
                ? 'Auction is already settled'
                : e.message?.includes('Only seller')
                    ? 'Only the seller can settle'
                    : (e.message || 'Settlement failed'), 'error');
        } finally {
            setSubmitting(false);
        }
    };

    // Cancel auction
    const handleCancel = async (auctionId) => {
        try {
            setSubmitting(true);
            notify('Cancelling auction...', 'warning');
            await (await contract.cancelAuction(auctionId)).wait();
            notify('Auction cancelled!');
            await loadData();
        } catch (e) {
            console.error('Cancel failed:', e);
            notify(e.message || 'Cannot cancel - has bids', 'error');
        } finally {
            setSubmitting(false);
        }
    };

    // Claim
    const handleClaim = async (bidId) => {
        try {
            setSubmitting(true);
            notify('Claiming tokens...', 'warning');
            await (await contract.claimTokens(bidId)).wait();
            notify('🎉 Tokens and refund claimed!');
            triggerConfetti();
            await loadData();
        } catch (e) {
            console.error('Claim failed:', e);
            notify(e.message || 'Claim failed', 'error');
        } finally {
            setSubmitting(false);
        }
    };

    // Withdraw proceeds (seller)
    const handleWithdraw = async (auctionId) => {
        try {
            setSubmitting(true);
            notify('Withdrawing proceeds (0.5% platform fee)...', 'warning');
            await (await contract.withdrawProceeds(auctionId)).wait();
            notify('Proceeds withdrawn! (0.5% fee deducted)');
            await loadData();
        } catch (e) {
            console.error('Withdraw failed:', e);
            if (e.message?.includes('Already withdrawn')) {
                notify('Already withdrawn', 'error');
            } else {
                notify(e.message || 'Withdraw failed', 'error');
            }
        } finally {
            setSubmitting(false);
        }
    };

    // Disconnect
    const disconnect = () => {
        setConnected(false);
        setAccount(null);
        setFheReady(false);
        setContract(null);
        setAuctions([]);
        setMyBids([]);
        setDecryptedPrices({});
        setAuctionBids({});
    };

    // Load bids for an auction (for leaderboard) - toggleable
    const loadAuctionBids = async (auctionId) => {
        // If already loaded, toggle visibility by clearing
        if (auctionBids[auctionId]) {
            setAuctionBids(prev => {
                const newState = { ...prev };
                delete newState[auctionId];
                return newState;
            });
            return;
        }

        if (loadingBids[auctionId]) return;

        try {
            setLoadingBids(prev => ({ ...prev, [auctionId]: true }));
            const bids = await fetchAuctionBids(contract, auctionId);
            console.log('Loaded bids for auction', auctionId, ':', bids);
            setAuctionBids(prev => ({ ...prev, [auctionId]: bids }));
        } catch (e) {
            console.error('Failed to load auction bids:', e);
        } finally {
            setLoadingBids(prev => ({ ...prev, [auctionId]: false }));
        }
    };

    // Mint test tokens (faucet)
    const handleMint = async () => {
        try {
            setSubmitting(true);
            notify('Minting test tokens...', 'warning');

            const provider = new ethers.BrowserProvider(window.ethereum);
            const signer = await provider.getSigner();
            const tokenContract = getERC20Contract(MOCK_TOKEN_ADDRESS, signer);
            const mintAmount = ethers.parseUnits('500000', 18); // 500k tokens

            await (await tokenContract.mint(account, mintAmount)).wait();
            notify('500,000 test tokens minted!');
        } catch (e) {
            console.error('Mint failed:', e);
            notify(e.message || 'Mint failed', 'error');
        } finally {
            setSubmitting(false);
        }
    };

    // Countdown
    const countdown = (endTime) => {
        const diff = endTime - Date.now();
        if (diff <= 0) return { expired: true };
        const d = Math.floor(diff / 86400000);
        const h = Math.floor((diff % 86400000) / 3600000);
        const m = Math.floor((diff % 3600000) / 60000);
        const s = Math.floor((diff % 60000) / 1000);
        return { expired: false, d, h, m, s };
    };

    const shortAddr = (a) => a ? `${a.slice(0, 6)}...${a.slice(-4)}` : '';

    // Tick + auto-refresh when auction ends (only once)
    const [, setTick] = useState(0);
    const refreshedAuctions = useRef(new Set());

    useEffect(() => {
        const i = setInterval(() => {
            setTick(t => t + 1);

            // Check if any active auction just ended (only refresh once per auction)
            const now = Date.now();
            const justEnded = auctions.find(a =>
                a.status === 'Active' && a.endTime <= now && !refreshedAuctions.current.has(a.id)
            );
            if (justEnded && contract) {
                refreshedAuctions.current.add(justEnded.id);
                loadData(); // Refresh once to get updated status
            }
        }, 1000);
        return () => clearInterval(i);
    }, [auctions, contract]);

    // Account change
    useEffect(() => {
        if (window.ethereum) {
            const handler = (accs) => {
                if (accs.length === 0) disconnect();
                else {
                    setAccount(accs[0]);
                    if (contract) loadData(contract, accs[0]);
                }
            };
            window.ethereum.on('accountsChanged', handler);
            return () => window.ethereum.removeListener('accountsChanged', handler);
        }
    }, [contract]);

    // ============ NOT CONNECTED ============
    if (!connected) {
        return (
            <div className="app">
                <header className="header">
                    <div className="brand">
                        <img src="/assets/auranode.png" alt="ShadeMarket" className="brand-logo-img" />
                        <div className="brand-text">
                            <h1>ShadeMarket</h1>
                            <span>Private Auctions</span>
                        </div>
                    </div>
                </header>

                <div className="connect-wrapper">
                    <div className="connect-card">
                        <div className="connect-icon"><img src="/assets/auranode.png" alt="" style={{ width: '80px', height: '80px' }} /></div>
                        <h2>Privacy-First Token Launch</h2>
                        <p>
                            Dutch auctions with encrypted bids. Your maximum price stays hidden until settlement. Fair price discovery guaranteed.
                        </p>
                        <div className="features">
                            <div className="feature">Encrypted Bids</div>
                            <div className="feature">Fair Pricing</div>
                            <div className="feature">Auto Refunds</div>
                        </div>
                        <button className="btn btn-buy" onClick={connect} disabled={loading} style={{ width: '100%' }}>
                            {loading ? <><span className="spin"></span> Connecting...</> : 'Connect Wallet'}
                        </button>
                    </div>
                </div>

                <div className="footer">
                    Powered by <a href="https://www.zama.ai/" target="_blank" rel="noopener noreferrer">Zama FHEVM</a>
                </div>

                {toast && <div className={`toast ${toast.type}`}>{toast.msg}</div>}
            </div>
        );
    }

    // ============ CONNECTED ============
    return (
        <div className="app">
            <header className="header">
                <div className="brand">
                    <img src="/assets/auranode.png" alt="ShadeMarket" className="brand-logo-img" />
                    <div className="brand-text">
                        <h1>ShadeMarket</h1>
                        <span>Private Auctions</span>
                    </div>
                </div>
                <div className="header-right">
                    <button className="btn btn-ghost btn-sm" onClick={handleMint} disabled={submitting} title="Get free test tokens">
                        {submitting ? <span className="spin"></span> : '🪙 Tokens'}
                    </button>
                    <button
                        className="btn btn-ghost btn-sm"
                        onClick={() => setDarkMode(!darkMode)}
                        title={darkMode ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
                    >
                        {darkMode ? '☀️' : '🌙'}
                    </button>
                    <div className="wallet-chip">
                        <div className="network-indicator">
                            <div className="network-dot"></div>
                            Sepolia
                        </div>
                        <span className="wallet-addr">{shortAddr(account)}</span>
                    </div>
                    <button className="btn btn-disconnect" onClick={disconnect} title="Disconnect">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4" />
                            <polyline points="16,17 21,12 16,7" />
                            <line x1="21" y1="12" x2="9" y2="12" />
                        </svg>
                    </button>
                </div>
            </header>

            <nav className="nav">
                <div className="tabs">
                    <button className={`tab ${tab === 'auctions' ? 'active' : ''}`} onClick={() => setTab('auctions')}>
                        <span>🏷️ Auctions</span>
                    </button>
                    <button className={`tab ${tab === 'mybids' ? 'active' : ''}`} onClick={() => setTab('mybids')}>
                        <span>📋 My Bids</span>
                    </button>
                    <button className={`tab ${tab === 'watchlist' ? 'active' : ''}`} onClick={() => setTab('watchlist')}>
                        <span>⭐ Watchlist ({Object.values(watchlist).filter(Boolean).length})</span>
                    </button>
                    <button className={`tab ${tab === 'analytics' ? 'active' : ''}`} onClick={() => setTab('analytics')}>
                        <span>📊 Analytics</span>
                    </button>
                </div>
                <div className="nav-spacer"></div>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <button className="btn btn-buy" onClick={() => setCreateModal(true)}>
                        + Create Auction
                    </button>
                </div>
            </nav>

            {/* Contract Warning */}
            {!isContractDeployed() && (
                <div className="info-box" style={{ borderColor: 'var(--error)', marginBottom: '24px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <span style={{ fontSize: '1.5rem' }}>!</span>
                        <div>
                            <strong style={{ color: 'var(--error)' }}>Contract not deployed</strong>
                            <div style={{ color: 'var(--gray-500)', fontSize: '0.85rem' }}>
                                Deploy to Sepolia and update frontend/.env with the contract address
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Auctions Tab */}
            {tab === 'auctions' && (
                <div className="main">
                    {auctions.length === 0 ? (
                        <div className="empty">
                            <div className="empty-icon">🎯</div>
                            <h3>No Auctions Yet</h3>
                            <p>Be the first to launch a confidential token auction!</p>
                            <button className="btn btn-buy" onClick={() => setCreateModal(true)}>
                                Create First Auction
                            </button>
                        </div>
                    ) : (
                        <div className="grid">
                            {auctions.map((auc) => {
                                const time = countdown(auc.endTime);
                                const isActive = auc.status === 'Active';
                                const isOwner = isSeller(auc);
                                return (
                                    <div key={auc.id} className="card">
                                        <div className="card-inner">
                                            <div className="auction-head">
                                                <div className="auction-title">
                                                    <h3>{auc.title}</h3>
                                                    <span className="symbol">${auc.tokenSymbol}</span>
                                                    {isOwner && <span className="symbol" style={{ marginLeft: '8px', background: 'var(--primary-bg)', color: 'var(--primary)' }}>Your Auction</span>}
                                                </div>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                    <button
                                                        className="btn btn-ghost"
                                                        style={{ padding: '6px 10px', fontSize: '1.1rem' }}
                                                        onClick={async (e) => {
                                                            e.stopPropagation();
                                                            try {
                                                                if (watchlist[auc.id]) {
                                                                    await contract.removeFromWatchlist(auc.id);
                                                                    setWatchlist(prev => ({ ...prev, [auc.id]: false }));
                                                                    notify('Removed from watchlist');
                                                                } else {
                                                                    await contract.addToWatchlist(auc.id);
                                                                    setWatchlist(prev => ({ ...prev, [auc.id]: true }));
                                                                    notify('Added to watchlist! ⭐');
                                                                }
                                                            } catch (err) {
                                                                notify(err.message?.includes('Already') ? 'Already in watchlist' : 'Watchlist error', 'error');
                                                            }
                                                        }}
                                                        title={watchlist[auc.id] ? 'Remove from watchlist' : 'Add to watchlist'}
                                                    >
                                                        {watchlist[auc.id] ? '⭐' : '☆'}
                                                    </button>
                                                    <span className={`status status-${auc.status.toLowerCase()}`}>{auc.status}</span>
                                                </div>
                                            </div>

                                            <div className="stats-grid">
                                                <div className="stat-box">
                                                    <div className="stat-label">Supply</div>
                                                    <div className="stat-value">{formatTokenAmount(auc.totalSupply)}</div>
                                                </div>
                                                <div className="stat-box">
                                                    <div className="stat-label">Min Price</div>
                                                    <div className="stat-value green">{formatEth(auc.minPriceWei)} ETH</div>
                                                </div>
                                                <div className="stat-box">
                                                    <div className="stat-label">Total Bids</div>
                                                    <div className="stat-value">{auc.totalBidCount}</div>
                                                </div>
                                                <div className="stat-box">
                                                    <div className="stat-label">Deposited</div>
                                                    <div className="stat-value">{formatEth(auc.totalDeposited)} ETH</div>
                                                </div>
                                            </div>

                                            {/* Auction Progress Bar */}
                                            {auc.status === 'Active' && auc.totalBidCount > 0 && (() => {
                                                const estimatedValue = Number(formatTokenAmount(auc.totalSupply)) * Number(formatEth(auc.minPriceWei));
                                                const deposited = Number(formatEth(auc.totalDeposited));
                                                const progress = estimatedValue > 0 ? Math.min((deposited / estimatedValue) * 100, 100) : 0;
                                                const isHot = progress > 75;
                                                return (
                                                    <div className="auction-progress">
                                                        <div className="progress-header">
                                                            <span className="progress-label">🔥 Auction Heat</span>
                                                            <span className="progress-value">{progress.toFixed(0)}%</span>
                                                        </div>
                                                        <div className="progress-bar">
                                                            <div className="progress-fill" style={{ width: `${progress}%` }}></div>
                                                        </div>
                                                        <div className="progress-heat">
                                                            <span>Min Value Est.</span>
                                                            <span className={`heat-indicator ${isHot ? 'hot' : ''}`}>
                                                                {isHot ? '🔥 High Demand' : 'Building...'}
                                                            </span>
                                                        </div>
                                                    </div>
                                                );
                                            })()}

                                            {/* FHE Encrypted Demand Indicator */}
                                            {auc.totalBidCount > 0 && (
                                                <div style={{
                                                    padding: '14px 18px',
                                                    background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.15), rgba(139, 92, 246, 0.08))',
                                                    borderRadius: '10px',
                                                    marginTop: '16px',
                                                    marginBottom: '16px',
                                                    border: '1px solid rgba(99, 102, 241, 0.3)',
                                                    display: 'flex',
                                                    justifyContent: 'space-between',
                                                    alignItems: 'center',
                                                    gap: '16px'
                                                }}>
                                                    <div style={{ flex: 1 }}>
                                                        <div style={{ fontSize: '0.85rem', color: 'var(--gray-300)', fontWeight: '500', marginBottom: '4px' }}>
                                                            🔐 Encrypted Total Demand
                                                        </div>
                                                        <div style={{ fontSize: '0.75rem', color: 'var(--gray-500)' }}>
                                                            Computed via <code style={{ color: 'var(--primary)', fontSize: '0.7rem', background: 'rgba(99,102,241,0.2)', padding: '2px 6px', borderRadius: '4px' }}>FHE.add()</code>
                                                        </div>
                                                    </div>
                                                    <div style={{
                                                        padding: '6px 14px',
                                                        background: 'var(--primary)',
                                                        borderRadius: '8px',
                                                        fontSize: '0.8rem',
                                                        fontWeight: 'bold',
                                                        color: 'white',
                                                        whiteSpace: 'nowrap'
                                                    }}>
                                                        🔒 HIDDEN
                                                    </div>
                                                </div>
                                            )}

                                            {/* Enhanced Timer with Urgency Indicators */}
                                            {!time.expired && auc.status === 'Active' && (
                                                <div className={`timer ${time.d === 0 && time.h < 1 ? 'timer-urgent' : time.d === 0 ? 'timer-warning' : ''}`}>
                                                    <div className="timer-unit">
                                                        <div className="timer-num">{String(time.d).padStart(2, '0')}</div>
                                                        <div className="timer-lbl">Days</div>
                                                    </div>
                                                    <div className="timer-unit">
                                                        <div className="timer-num">{String(time.h).padStart(2, '0')}</div>
                                                        <div className="timer-lbl">Hours</div>
                                                    </div>
                                                    <div className="timer-unit">
                                                        <div className="timer-num">{String(time.m).padStart(2, '0')}</div>
                                                        <div className="timer-lbl">Mins</div>
                                                    </div>
                                                    <div className="timer-unit">
                                                        <div className="timer-num">{String(time.s).padStart(2, '0')}</div>
                                                        <div className="timer-lbl">Secs</div>
                                                    </div>
                                                </div>
                                            )}

                                            {/* FHE Encrypted Stats - Phase 3 Feature */}
                                            {auc.totalBidCount > 0 && auc.status === 'Active' && (
                                                <div className="fhe-stats">
                                                    <div className="fhe-stat">
                                                        <span className="fhe-icon">🔐</span>
                                                        <div className="fhe-info">
                                                            <span className="fhe-label">Hidden Demand ({auc.totalBidCount} {auc.totalBidCount === 1 ? 'bidder' : 'bidders'})</span>
                                                            <span className="fhe-value encrypted">{'█'.repeat(Math.min(Number(auc.totalBidCount) * 2 + 4, 16))}</span>
                                                        </div>
                                                    </div>
                                                    <div className="fhe-stat">
                                                        <span className="fhe-icon">📊</span>
                                                        <div className="fhe-info">
                                                            <span className="fhe-label">Public Prices</span>
                                                            <span className="fhe-value">{auc.totalBidCount} visible • quantities hidden</span>
                                                        </div>
                                                    </div>

                                                    {/* Bid Leaderboard - Phase 4 */}
                                                    <div className="bid-leaderboard">
                                                        <div className="leaderboard-header" onClick={() => loadAuctionBids(auc.id)}>
                                                            <span className="leaderboard-title">🏆 Price Leaderboard</span>
                                                            {loadingBids[auc.id] ? (
                                                                <span className="spin" style={{ width: 14, height: 14 }}></span>
                                                            ) : auctionBids[auc.id] ? (
                                                                <span className="leaderboard-toggle">▲</span>
                                                            ) : (
                                                                <span className="leaderboard-toggle">▼ View</span>
                                                            )}
                                                        </div>
                                                        {auctionBids[auc.id] && (
                                                            <div className="leaderboard-list">
                                                                {auctionBids[auc.id].slice(0, 5).map((bid, idx) => (
                                                                    <div key={bid.id} className={`leaderboard-item ${bid.bidder.toLowerCase() === account?.toLowerCase() ? 'my-bid' : ''}`}>
                                                                        <span className={`rank rank-${idx + 1}`}>
                                                                            {idx === 0 ? '🥇' : idx === 1 ? '🥈' : idx === 2 ? '🥉' : `#${idx + 1}`}
                                                                        </span>
                                                                        <span className="bid-price">{formatEth(bid.priceWei)} ETH</span>
                                                                        <span className="bid-qty encrypted">█████</span>
                                                                        {bid.bidder.toLowerCase() === account?.toLowerCase() && (
                                                                            <span className="you-badge">YOU</span>
                                                                        )}
                                                                    </div>
                                                                ))}
                                                                {auctionBids[auc.id].length > 5 && (
                                                                    <div className="leaderboard-more">
                                                                        +{auctionBids[auc.id].length - 5} more bids
                                                                    </div>
                                                                )}
                                                            </div>
                                                        )}
                                                    </div>

                                                    <div className="fhe-hint">
                                                        Quantities are FHE encrypted • Revealed at settlement
                                                    </div>
                                                </div>
                                            )}

                                            {/* Settlement Results - Show after settled */}
                                            {auc.status === 'Settled' && auc.clearingPriceWei > 0n && (
                                                <div className="settlement-results">
                                                    <div className="result-item">
                                                        <span className="result-label">🎯 Clearing Price</span>
                                                        <span className="result-value">{formatEth(auc.clearingPriceWei)} ETH</span>
                                                    </div>
                                                    <div className="result-item">
                                                        <span className="result-label">📦 Tokens Sold</span>
                                                        <span className="result-value">{auc.filledSupply?.toString() || '0'} / {formatTokenAmount(auc.totalSupply)}</span>
                                                    </div>
                                                </div>
                                            )}

                                            {isOwner ? (
                                                <div style={{ display: 'flex', gap: '8px', flexDirection: 'column' }}>
                                                    {/* Seller actions based on auction state */}
                                                    {isActive && Number(auc.totalBidCount) === 0 && (
                                                        <button
                                                            className="btn btn-ghost"
                                                            style={{ width: '100%' }}
                                                            onClick={() => handleCancel(auc.id)}
                                                            disabled={submitting}
                                                        >
                                                            {submitting ? <span className="spin"></span> : 'Cancel Auction'}
                                                        </button>
                                                    )}
                                                    {isActive && Number(auc.totalBidCount) > 0 && (
                                                        <button className="btn btn-ghost" style={{ width: '100%' }} disabled>
                                                            Waiting for auction end ({auc.totalBidCount} bids)
                                                        </button>
                                                    )}
                                                    {!isActive && auc.status === 'Ended' && (
                                                        <button
                                                            className="btn btn-buy"
                                                            style={{ width: '100%' }}
                                                            onClick={() => handleSettle(auc.id)}
                                                            disabled={submitting}
                                                        >
                                                            {submitting ? <><span className="spin"></span> Settling...</> : 'Settle Auction'}
                                                        </button>
                                                    )}
                                                    {auc.status === 'Settled' && !withdrawnAuctions[auc.id] && (
                                                        <button
                                                            className="btn btn-buy"
                                                            style={{ width: '100%' }}
                                                            onClick={() => handleWithdraw(auc.id)}
                                                            disabled={submitting}
                                                        >
                                                            {submitting ? <><span className="spin"></span> Withdrawing...</> : 'Withdraw Proceeds (0.5% fee)'}
                                                        </button>
                                                    )}
                                                    {auc.status === 'Settled' && withdrawnAuctions[auc.id] && (
                                                        <button className="btn btn-ghost" style={{ width: '100%' }} disabled>
                                                            Already Withdrawn
                                                        </button>
                                                    )}
                                                    {auc.status === 'Cancelled' && (
                                                        <button className="btn btn-ghost" style={{ width: '100%' }} disabled>
                                                            Tokens Returned
                                                        </button>
                                                    )}
                                                </div>
                                            ) : (
                                                <button
                                                    className={`btn ${isActive ? 'btn-buy' : 'btn-ghost'}`}
                                                    style={{ width: '100%' }}
                                                    disabled={!isActive}
                                                    onClick={() => { setSelectedAuction(auc); setBidModal(true); }}
                                                >
                                                    {isActive
                                                        ? 'Place Encrypted Bid'
                                                        : auc.status === 'Settled'
                                                            ? 'Auction Settled'
                                                            : auc.status === 'Cancelled'
                                                                ? 'Auction Cancelled'
                                                                : 'Awaiting Settlement'}
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            )}

            {/* My Bids Tab */}
            {tab === 'mybids' && (
                <div className="main">
                    {myBids.length === 0 ? (
                        <div className="empty">
                            <div className="empty-icon">📋</div>
                            <h3>No Bids Yet</h3>
                            <p>Place your first encrypted bid on an auction.</p>
                            <button className="btn btn-buy" onClick={() => setTab('auctions')}>
                                Browse Auctions
                            </button>
                        </div>
                    ) : (
                        <>
                            {/* Batch Claim Header */}
                            {myBids.filter(b => !b.claimed && b.auctionStatus === 'Settled' && (b.filledQuantity > 0n || b.refundAmount > 0n)).length > 1 && (
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', padding: '12px 16px', background: 'var(--card-bg)', borderRadius: '12px', border: '1px solid var(--border)' }}>
                                    <span style={{ color: 'var(--gray-400)' }}>
                                        {myBids.filter(b => !b.claimed && b.auctionStatus === 'Settled' && (b.filledQuantity > 0n || b.refundAmount > 0n)).length} bids ready to claim
                                    </span>
                                    <button
                                        className="btn btn-buy"
                                        onClick={async () => {
                                            const claimableBidIds = myBids
                                                .filter(b => !b.claimed && b.auctionStatus === 'Settled' && (b.filledQuantity > 0n || b.refundAmount > 0n))
                                                .map(b => b.id);
                                            if (claimableBidIds.length === 0) {
                                                notify('No bids to claim', 'error');
                                                return;
                                            }
                                            setSubmitting(true);
                                            try {
                                                notify('🔄 Batch claiming...', 'warning');
                                                await (await contract.batchClaimTokens(claimableBidIds)).wait();
                                                notify(`🎉 Claimed ${claimableBidIds.length} bids!`);
                                                await loadData();
                                            } catch (err) {
                                                notify(err.reason || 'Batch claim failed', 'error');
                                            }
                                            setSubmitting(false);
                                        }}
                                        disabled={submitting}
                                    >
                                        {submitting ? <span className="spin"></span> : '🎁'} Claim All ({myBids.filter(b => !b.claimed && b.auctionStatus === 'Settled' && (b.filledQuantity > 0n || b.refundAmount > 0n)).length})
                                    </button>
                                </div>
                            )}

                            {/* Compare Bids FHE Feature */}
                            {myBids.length >= 2 && (
                                <div style={{ marginBottom: '16px', padding: '16px 20px', background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.1), rgba(139, 92, 246, 0.05))', borderRadius: '12px', border: '1px solid var(--primary)' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
                                        <span style={{ fontSize: '1.5rem' }}>🔒</span>
                                        <div>
                                            <h4 style={{ margin: 0 }}>Compare Bids (Encrypted)</h4>
                                            <p style={{ margin: '4px 0 0 0', fontSize: '0.8rem', color: 'var(--gray-400)' }}>
                                                Uses <code style={{ color: 'var(--primary)' }}>FHE.gt()</code> to compare encrypted quantities without revealing values
                                            </p>
                                        </div>
                                    </div>
                                    <div style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
                                        <select
                                            id="bid1-select"
                                            style={{ padding: '8px 12px', borderRadius: '8px', background: 'var(--card-bg)', border: '1px solid var(--border)', color: 'var(--foreground)', flex: 1, minWidth: '120px' }}
                                        >
                                            {myBids.map((bid, idx) => (
                                                <option key={bid.id} value={bid.id}>Bid #{bid.id} ({bid.auctionTitle || `Auction ${bid.auctionId}`})</option>
                                            ))}
                                        </select>
                                        <span style={{ color: 'var(--gray-400)' }}>vs</span>
                                        <select
                                            id="bid2-select"
                                            style={{ padding: '8px 12px', borderRadius: '8px', background: 'var(--card-bg)', border: '1px solid var(--border)', color: 'var(--foreground)', flex: 1, minWidth: '120px' }}
                                            defaultValue={myBids.length > 1 ? myBids[1].id : myBids[0].id}
                                        >
                                            {myBids.map((bid, idx) => (
                                                <option key={bid.id} value={bid.id}>Bid #{bid.id} ({bid.auctionTitle || `Auction ${bid.auctionId}`})</option>
                                            ))}
                                        </select>
                                        <button
                                            className="btn btn-ghost"
                                            style={{ borderColor: 'var(--primary)', color: 'var(--primary)' }}
                                            onClick={async () => {
                                                const bid1Id = document.getElementById('bid1-select').value;
                                                const bid2Id = document.getElementById('bid2-select').value;
                                                if (bid1Id === bid2Id) {
                                                    notify('Select two different bids', 'error');
                                                    return;
                                                }

                                                // Find the bid details
                                                const bid1 = myBids.find(b => String(b.id) === String(bid1Id));
                                                const bid2 = myBids.find(b => String(b.id) === String(bid2Id));

                                                if (!bid1 || !bid2) {
                                                    notify('Could not find bid details', 'error');
                                                    return;
                                                }

                                                try {
                                                    notify('🔒 Comparing encrypted quantities on-chain...', 'warning');
                                                    const tx = await contract.compareMyBidQuantities(bid1Id, bid2Id);
                                                    const receipt = await tx.wait();

                                                    // Get resultKey from ComparisonPerformed event
                                                    let resultKey = null;

                                                    // Parse logs to find ComparisonPerformed event
                                                    for (const log of receipt.logs) {
                                                        try {
                                                            // ComparisonPerformed event has 4 topics (event sig + 3 indexed)
                                                            // and data contains resultKey
                                                            if (log.topics && log.topics.length >= 3) {
                                                                // The resultKey is in the last indexed topic
                                                                if (log.topics.length === 4) {
                                                                    // resultKey is the 4th topic (index 3) since all params are indexed
                                                                    resultKey = log.data || log.topics[3];
                                                                } else {
                                                                    // Try data field
                                                                    resultKey = log.data;
                                                                }
                                                                if (resultKey && resultKey !== '0x') break;
                                                            }
                                                        } catch (e) { /* ignore parse errors */ }
                                                    }

                                                    console.log('Comparison Result Key:', resultKey);

                                                    // Show comparison modal with bid details
                                                    setCompareModal({
                                                        bid1: bid1,
                                                        bid2: bid2,
                                                        txHash: receipt.hash || tx.hash,
                                                        resultKey: resultKey,
                                                        success: true
                                                    });
                                                    notify('✅ Encrypted comparison complete! Click Decrypt to see FHE result.');
                                                } catch (err) {
                                                    notify(err.reason || 'Comparison failed', 'error');
                                                }
                                            }}
                                        >
                                            🔒 Compare
                                        </button>
                                    </div>
                                </div>
                            )}

                            <div className="grid">
                                {myBids.map((bid) => (
                                    <div key={bid.id} className="card">
                                        <div className="card-inner">
                                            <div className="auction-head">
                                                <div className="auction-title">
                                                    <h3>{bid.auctionTitle || `Auction #${bid.auctionId}`}</h3>
                                                    <span className="symbol">${bid.tokenSymbol || 'TOKEN'}</span>
                                                </div>
                                                <span className={`status ${bid.claimed ? 'status-settled' :
                                                    bid.auctionStatus === 'Settled' && bid.filledQuantity > 0n && bid.filledQuantity >= bid.revealedQuantity ? 'status-success' :
                                                        bid.auctionStatus === 'Settled' && bid.filledQuantity > 0n ? 'status-partial' :
                                                            bid.auctionStatus === 'Settled' && bid.filledQuantity === 0n ? 'status-failed' :
                                                                bid.auctionStatus === 'Ended' ? 'status-warning' :
                                                                    'status-active'
                                                    }`}>
                                                    {bid.claimed ? '✓ Claimed' :
                                                        bid.auctionStatus === 'Settled' && bid.filledQuantity > 0n && bid.filledQuantity >= bid.revealedQuantity ? '🎉 Fully Allocated' :
                                                            bid.auctionStatus === 'Settled' && bid.filledQuantity > 0n ? '📊 Partially Allocated' :
                                                                bid.auctionStatus === 'Settled' && bid.filledQuantity === 0n ? '❌ Not Allocated' :
                                                                    bid.auctionStatus === 'Ended' ? '⏳ Awaiting Settlement' :
                                                                        '🔒 Bidding'}
                                                </span>
                                            </div>

                                            <div className="info-box">
                                                <div className="info-row">
                                                    <span className="info-label">Your Bid Price</span>
                                                    <span className="info-value">
                                                        <span className="green">{formatEth(bid.priceWei)} ETH</span>
                                                        {/* <span style={{ fontSize: '11px', color: 'var(--gray-400)', marginLeft: '8px' }}></span> */}
                                                    </span>
                                                </div>
                                                <div className="info-row">
                                                    <span className="info-label">Quantity</span>
                                                    <span className="info-value">
                                                        {bid.revealedQuantity > 0n
                                                            ? <span className="green">{bid.revealedQuantity.toString()} tokens</span>
                                                            : decryptedPrices[bid.id] !== undefined
                                                                ? <span className="green">{decryptedPrices[bid.id].toString()} tokens</span>
                                                                : <span style={{ color: 'var(--gray-400)' }}>Encrypted</span>
                                                        }
                                                    </span>
                                                </div>
                                                <div className="info-row">
                                                    <span className="info-label">Deposited</span>
                                                    <span className="info-value">{formatEth(bid.depositedWei)} ETH</span>
                                                </div>

                                                {/* Show outcome for ALL settled bids - including 0 allocation */}
                                                {bid.auctionStatus === 'Settled' && (
                                                    <>
                                                        <div className="info-row">
                                                            <span className="info-label">Filled</span>
                                                            <span className={`info-value ${bid.filledQuantity > 0n ? 'green' : 'red'}`}>
                                                                {bid.filledQuantity.toString()} tokens
                                                                {bid.revealedQuantity > 0n && bid.filledQuantity < bid.revealedQuantity &&
                                                                    <span style={{ fontSize: '11px', color: 'var(--gray-400)', marginLeft: '4px' }}>
                                                                        (of {bid.revealedQuantity.toString()} requested)
                                                                    </span>
                                                                }
                                                            </span>
                                                        </div>
                                                        {/* Show clearing price to explain refund */}
                                                        {bid.auctionClearingPrice && bid.auctionClearingPrice > 0n && (
                                                            <div className="info-row">
                                                                <span className="info-label">Clearing Price</span>
                                                                <span className="info-value">
                                                                    {formatEth(bid.auctionClearingPrice)} ETH
                                                                    {bid.priceWei > bid.auctionClearingPrice && (
                                                                        <span style={{ fontSize: '10px', color: 'var(--success)', marginLeft: '4px' }}>
                                                                            (you save {formatEth(bid.priceWei - bid.auctionClearingPrice)}/token!)
                                                                        </span>
                                                                    )}
                                                                </span>
                                                            </div>
                                                        )}
                                                        <div className="info-row">
                                                            <span className="info-label">Refund <span style={{ fontSize: '10px', color: 'var(--gray-400)', fontWeight: 'normal' }}>(price diff × qty)</span></span>
                                                            <span className={`info-value ${bid.refundAmount > 0n ? 'green' : ''}`}>
                                                                {formatEth(bid.refundAmount)} ETH
                                                            </span>
                                                        </div>
                                                    </>
                                                )}
                                            </div>

                                            {/* Preview Quantity Button - just local decrypt, no contract call */}
                                            {!bid.claimed && decryptedPrices[bid.id] === undefined && bid.revealedQuantity === 0n && bid.auctionStatus !== 'Settled' && (
                                                <button
                                                    className="btn btn-ghost"
                                                    style={{ width: '100%', marginBottom: '10px' }}
                                                    onClick={() => handlePreviewBid(bid.id, bid.auctionId)}
                                                    disabled={decrypting[bid.id]}
                                                >
                                                    {decrypting[bid.id]
                                                        ? <><span className="spin"></span> Decrypting...</>
                                                        : 'Preview My Quantity'
                                                    }
                                                </button>
                                            )}

                                            {/* Show decrypted quantity if previewed */}
                                            {decryptedPrices[bid.id] !== undefined && bid.revealedQuantity === 0n && bid.auctionStatus !== 'Settled' && (
                                                <div className="info-box" style={{ marginBottom: '10px' }}>
                                                    <span>Your Quantity: <strong className="green">{decryptedPrices[bid.id].toString()} tokens</strong></span>
                                                </div>
                                            )}

                                            {/* Awaiting Settlement status (after auction ends, before settlement) */}
                                            {!bid.claimed && bid.auctionStatus === 'Ended' && bid.revealedQuantity === 0n && (
                                                <div className="info-box" style={{ marginBottom: '10px', background: 'var(--yellow-bg)', borderColor: 'var(--yellow)' }}>
                                                    <span style={{ color: 'var(--yellow)' }}>⏳ Awaiting Seller Settlement</span>
                                                </div>
                                            )}

                                            {/* Claim Tokens & Refund Button - for bids with tokens allocated */}
                                            {!bid.claimed && bid.auctionStatus === 'Settled' && bid.filledQuantity > 0n && (
                                                <button className="btn btn-buy" style={{ width: '100%' }} onClick={() => handleClaim(bid.id)} disabled={submitting}>
                                                    {submitting ? <span className="spin"></span> : '🎁 Claim Tokens & Refund'}
                                                </button>
                                            )}

                                            {/* Claim Refund Only Button - for bids with 0 tokens but refund available */}
                                            {!bid.claimed && bid.auctionStatus === 'Settled' && bid.filledQuantity === 0n && bid.refundAmount > 0n && (
                                                <button className="btn btn-ghost" style={{ width: '100%', borderColor: 'var(--success)', color: 'var(--success)' }} onClick={() => handleClaim(bid.id)} disabled={submitting}>
                                                    {submitting ? <span className="spin"></span> : '💰 Claim Full Refund'}
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </>
                    )}
                </div>
            )}

            {/* Watchlist Tab - Production FHE Feature */}
            {tab === 'watchlist' && (
                <div className="main">
                    {/* FHE Encrypted Count Header */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px', padding: '16px 20px', background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.1), rgba(139, 92, 246, 0.1))', borderRadius: '12px', border: '1px solid var(--primary)' }}>
                        <div>
                            <h2 style={{ margin: 0 }}>⭐ My Private Watchlist</h2>
                            <p style={{ margin: '8px 0 0 0', color: 'var(--gray-400)', fontSize: '0.85rem' }}>
                                🔐 Your watchlist count is encrypted on-chain using <code style={{ color: 'var(--primary)' }}>FHE.add()/sub()</code>
                            </p>
                        </div>
                        <div style={{ textAlign: 'right' }}>
                            <div style={{ fontSize: '2rem', fontWeight: 'bold', color: 'var(--primary)' }}>
                                {Object.values(watchlist).filter(Boolean).length}
                            </div>
                            <div style={{ fontSize: '0.75rem', color: 'var(--gray-400)' }}>Watching</div>
                        </div>
                    </div>

                    {Object.values(watchlist).filter(Boolean).length === 0 ? (
                        <div className="empty">
                            <div className="empty-icon">⭐</div>
                            <h3>No Auctions in Watchlist</h3>
                            <p>Click the ☆ star on any auction card to add it to your private watchlist.</p>
                            <p style={{ color: 'var(--gray-500)', fontSize: '0.85rem', marginTop: '12px' }}>
                                🔐 Your watchlist count is encrypted - no one can see how many auctions you're watching!
                            </p>
                            <button className="btn btn-buy" onClick={() => setTab('auctions')}>
                                Browse Auctions
                            </button>
                        </div>
                    ) : (
                        <div className="grid">
                            {auctions.filter(auc => watchlist[auc.id]).map((auc) => {
                                const isOwner = isSeller(auc);
                                return (
                                    <div key={auc.id} className="card" style={{ border: '1px solid var(--primary)' }}>
                                        <div className="card-inner">
                                            <div className="auction-head">
                                                <div className="auction-title">
                                                    <h3>{auc.title}</h3>
                                                    <span className="symbol">${auc.tokenSymbol}</span>
                                                    <span className="symbol" style={{ marginLeft: '8px', background: 'var(--primary-bg)', color: 'var(--primary)' }}>⭐ Watching</span>
                                                </div>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                    <button
                                                        className="btn btn-ghost"
                                                        style={{ padding: '6px 10px', fontSize: '1.1rem', color: 'var(--error)' }}
                                                        onClick={async (e) => {
                                                            e.stopPropagation();
                                                            try {
                                                                await contract.removeFromWatchlist(auc.id);
                                                                setWatchlist(prev => ({ ...prev, [auc.id]: false }));
                                                                notify('Removed from watchlist');
                                                            } catch (err) {
                                                                notify(err.reason || 'Failed to remove', 'error');
                                                            }
                                                        }}
                                                        title="Remove from watchlist"
                                                    >
                                                        ✕
                                                    </button>
                                                    <span className={`status status-${auc.status.toLowerCase()}`}>{auc.status}</span>
                                                </div>
                                            </div>

                                            <div className="stats-grid">
                                                <div className="stat-box">
                                                    <div className="stat-label">Supply</div>
                                                    <div className="stat-value">{formatTokenAmount(auc.totalSupply)}</div>
                                                </div>
                                                <div className="stat-box">
                                                    <div className="stat-label">Min Price</div>
                                                    <div className="stat-value green">{formatEth(auc.minPriceWei)} ETH</div>
                                                </div>
                                                <div className="stat-box">
                                                    <div className="stat-label">Total Bids</div>
                                                    <div className="stat-value">{auc.totalBidCount}</div>
                                                </div>
                                                <div className="stat-box">
                                                    <div className="stat-label">Deposited</div>
                                                    <div className="stat-value">{formatEth(auc.totalDeposited)} ETH</div>
                                                </div>
                                            </div>

                                            {/* Time remaining */}
                                            {auc.status === 'Active' && (
                                                <div style={{ padding: '12px 16px', background: 'var(--info-bg)', borderRadius: '8px', marginTop: '12px' }}>
                                                    <span style={{ color: 'var(--info)' }}>⏰ Ends: {new Date(Number(auc.endTime) * 1000).toLocaleString()}</span>
                                                </div>
                                            )}

                                            {/* Bid button */}
                                            {auc.status === 'Active' && !isOwner && !hasBid[auc.id] && (
                                                <button
                                                    className="btn btn-buy"
                                                    style={{ width: '100%', marginTop: '12px' }}
                                                    onClick={() => { setSelectedAuction(auc); setBidModal(true); }}
                                                >
                                                    🔐 Place Encrypted Bid
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}

                    {/* FHE Info Box */}
                    <div style={{ marginTop: '24px', padding: '16px 20px', background: 'var(--card-bg)', borderRadius: '12px', border: '1px solid var(--border)' }}>
                        <h4 style={{ marginBottom: '8px' }}>🔐 How Private Watchlist Works</h4>
                        <p style={{ color: 'var(--gray-400)', fontSize: '0.85rem', margin: 0, lineHeight: 1.6 }}>
                            When you add an auction to your watchlist, the contract calls <code style={{ color: 'var(--primary)' }}>FHE.add(userWatchlistCount, 1)</code> to increment your encrypted count.
                            When you remove, it calls <code style={{ color: 'var(--primary)' }}>FHE.sub()</code>.
                            <strong> No one can see your actual watchlist count</strong> - it stays encrypted on-chain!
                        </p>
                    </div>
                </div>
            )}

            {/* Analytics Tab */}
            {tab === 'analytics' && (
                <div className="main">
                    <div className="analytics-container">
                        <h2 style={{ marginBottom: '24px' }}>📊 Platform Analytics</h2>

                        {/* Platform Stats */}
                        <div className="stats-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', marginBottom: '32px' }}>
                            <div className="stat-card" style={{ background: 'var(--card-bg)', padding: '20px', borderRadius: '12px', border: '1px solid var(--border)' }}>
                                <div style={{ color: 'var(--gray-400)', fontSize: '0.85rem', marginBottom: '8px' }}>Total Auctions</div>
                                <div style={{ fontSize: '2rem', fontWeight: 'bold', color: 'var(--primary)' }}>{auctions.length}</div>
                            </div>
                            <div className="stat-card" style={{ background: 'var(--card-bg)', padding: '20px', borderRadius: '12px', border: '1px solid var(--border)' }}>
                                <div style={{ color: 'var(--gray-400)', fontSize: '0.85rem', marginBottom: '8px' }}>Active Auctions</div>
                                <div style={{ fontSize: '2rem', fontWeight: 'bold', color: 'var(--success)' }}>{auctions.filter(a => a.status === 'Active').length}</div>
                            </div>
                            <div className="stat-card" style={{ background: 'var(--card-bg)', padding: '20px', borderRadius: '12px', border: '1px solid var(--border)' }}>
                                <div style={{ color: 'var(--gray-400)', fontSize: '0.85rem', marginBottom: '8px' }}>Your Bids</div>
                                <div style={{ fontSize: '2rem', fontWeight: 'bold', color: 'var(--accent)' }}>{myBids.length}</div>
                            </div>
                            <div className="stat-card" style={{ background: 'var(--card-bg)', padding: '20px', borderRadius: '12px', border: '1px solid var(--border)' }}>
                                <div style={{ color: 'var(--gray-400)', fontSize: '0.85rem', marginBottom: '8px' }}>Settled Auctions</div>
                                <div style={{ fontSize: '2rem', fontWeight: 'bold', color: 'var(--yellow)' }}>{auctions.filter(a => a.status === 'Settled').length}</div>
                            </div>
                        </div>

                        {/* Your Activity Summary */}
                        <h3 style={{ marginBottom: '16px' }}>Your Activity</h3>
                        <div className="activity-summary" style={{ background: 'var(--card-bg)', padding: '20px', borderRadius: '12px', border: '1px solid var(--border)', marginBottom: '24px' }}>
                            {myBids.length === 0 ? (
                                <div style={{ color: 'var(--gray-400)', textAlign: 'center', padding: '20px' }}>
                                    <p>No bids placed yet</p>
                                    <button className="btn btn-buy" style={{ marginTop: '12px' }} onClick={() => setTab('auctions')}>Browse Auctions</button>
                                </div>
                            ) : (
                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '16px' }}>
                                    <div>
                                        <div style={{ color: 'var(--gray-400)', fontSize: '0.85rem' }}>Total Deposited</div>
                                        <div style={{ fontSize: '1.25rem', fontWeight: 'bold' }}>{formatEth(myBids.reduce((acc, b) => acc + b.depositedWei, 0n))} ETH</div>
                                    </div>
                                    <div>
                                        <div style={{ color: 'var(--gray-400)', fontSize: '0.85rem' }}>Pending Claims</div>
                                        <div style={{ fontSize: '1.25rem', fontWeight: 'bold' }}>{myBids.filter(b => !b.claimed && b.auctionStatus === 'Settled').length}</div>
                                    </div>
                                    <div>
                                        <div style={{ color: 'var(--gray-400)', fontSize: '0.85rem' }}>Claimed</div>
                                        <div style={{ fontSize: '1.25rem', fontWeight: 'bold' }}>{myBids.filter(b => b.claimed).length}</div>
                                    </div>
                                    <div>
                                        <div style={{ color: 'var(--gray-400)', fontSize: '0.85rem' }}>Active Bids</div>
                                        <div style={{ fontSize: '1.25rem', fontWeight: 'bold' }}>{myBids.filter(b => b.auctionStatus === 'Active').length}</div>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* FHE Features Info */}
                        <h3 style={{ marginBottom: '16px' }}>🔐 FHE Features</h3>
                        <div className="fhe-features" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '16px' }}>
                            {/* Generate Random Demo */}
                            <div className="feature-card" style={{ background: 'var(--card-bg)', padding: '20px', borderRadius: '12px', border: '1px solid var(--border)' }}>
                                <div style={{ fontSize: '1.5rem', marginBottom: '8px' }}>🎲</div>
                                <h4 style={{ marginBottom: '8px' }}>FHE Random Tie-Breaking</h4>
                                <p style={{ color: 'var(--gray-400)', fontSize: '0.85rem', marginBottom: '12px' }}>Uses FHE.randEuint64() for fair, on-chain randomness</p>
                                <button
                                    className="btn btn-ghost"
                                    style={{ width: '100%' }}
                                    onClick={async () => {
                                        try {
                                            notify('🎲 Generating encrypted random...', 'warning');
                                            const tx = await contract.generateEncryptedRandom();
                                            await tx.wait();
                                            notify('✅ FHE random generated on-chain! Check console for handle.');
                                            console.log('FHE Random handle:', tx);
                                        } catch (err) {
                                            notify(err.reason || 'Random generation failed', 'error');
                                        }
                                    }}
                                    disabled={!contract}
                                >
                                    🎲 Generate Random
                                </button>
                            </div>

                            {/* Check Encrypted Demand Demo */}
                            <div className="feature-card" style={{ background: 'var(--card-bg)', padding: '20px', borderRadius: '12px', border: '1px solid var(--border)' }}>
                                <div style={{ fontSize: '1.5rem', marginBottom: '8px' }}>📊</div>
                                <h4 style={{ marginBottom: '8px' }}>Encrypted Total Demand</h4>
                                <p style={{ color: 'var(--gray-400)', fontSize: '0.85rem', marginBottom: '12px' }}>Aggregate demand via FHE.add() on encrypted quantities</p>
                                <button
                                    className="btn btn-ghost"
                                    style={{ width: '100%' }}
                                    onClick={async () => {
                                        try {
                                            if (auctions.length === 0) {
                                                notify('No auctions to check', 'error');
                                                return;
                                            }
                                            const auctionId = auctions[0].id;
                                            notify(`📊 Getting encrypted demand for Auction #${auctionId}...`, 'warning');
                                            const handle = await contract.getEncryptedTotalDemand(auctionId);
                                            notify(`✅ Encrypted demand handle: ${handle.toString().slice(0, 20)}...`);
                                            console.log('Encrypted Total Demand Handle:', handle.toString());
                                        } catch (err) {
                                            notify(err.reason || 'Failed to get demand', 'error');
                                        }
                                    }}
                                    disabled={!contract || auctions.length === 0}
                                >
                                    📊 Check Demand (Auction #1)
                                </button>
                            </div>

                            {/* Private Watchlist Demo */}
                            <div className="feature-card" style={{ background: 'var(--card-bg)', padding: '20px', borderRadius: '12px', border: '1px solid var(--border)' }}>
                                <div style={{ fontSize: '1.5rem', marginBottom: '8px' }}>⭐</div>
                                <h4 style={{ marginBottom: '8px' }}>Private Watchlist</h4>
                                <p style={{ color: 'var(--gray-400)', fontSize: '0.85rem', marginBottom: '12px' }}>Watchlist count encrypted with FHE.add()/sub()</p>
                                <button
                                    className="btn btn-ghost"
                                    style={{ width: '100%' }}
                                    onClick={async () => {
                                        try {
                                            notify('⭐ Getting your encrypted watchlist count...', 'warning');
                                            const handle = await contract.getMyWatchlistCount();
                                            notify(`✅ Encrypted count handle: ${handle.toString().slice(0, 20)}...`);
                                            console.log('Encrypted Watchlist Count:', handle.toString());
                                        } catch (err) {
                                            notify(err.reason || 'Failed to get count', 'error');
                                        }
                                    }}
                                    disabled={!contract}
                                >
                                    ⭐ Get My Watchlist Count
                                </button>
                            </div>

                            {/* Compare Bids Demo */}
                            <div className="feature-card" style={{ background: 'var(--card-bg)', padding: '20px', borderRadius: '12px', border: '1px solid var(--border)' }}>
                                <div style={{ fontSize: '1.5rem', marginBottom: '8px' }}>🔒</div>
                                <h4 style={{ marginBottom: '8px' }}>Encrypted Comparisons</h4>
                                <p style={{ color: 'var(--gray-400)', fontSize: '0.85rem', marginBottom: '12px' }}>Compare bids without revealing using FHE.gt()</p>
                                <button
                                    className="btn btn-ghost"
                                    style={{ width: '100%' }}
                                    onClick={async () => {
                                        try {
                                            if (myBids.length < 2) {
                                                notify('Need 2+ bids to compare', 'error');
                                                return;
                                            }
                                            notify('🔒 Comparing your first two bids...', 'warning');
                                            const result = await contract.compareMyBidQuantities(myBids[0].id, myBids[1].id);
                                            notify(`✅ Comparison result (encrypted ebool): ${result.toString().slice(0, 20)}...`);
                                            console.log('Encrypted Comparison Result:', result.toString());
                                        } catch (err) {
                                            notify(err.reason || 'Comparison failed', 'error');
                                        }
                                    }}
                                    disabled={!contract || myBids.length < 2}
                                >
                                    🔒 Compare My Bids
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Create Modal */}
            {createModal && (
                <div className="modal-overlay" onClick={() => !submitting && setCreateModal(false)}>
                    <div className="modal" onClick={e => e.stopPropagation()}>
                        <div className="modal-header">
                            <h3>🎯 Create Auction</h3>
                            <button className="modal-close" onClick={() => setCreateModal(false)}>×</button>
                        </div>
                        <div className="modal-body">
                            <div className="field">
                                <label>Auction Title *</label>
                                <input placeholder="e.g., MYTOKEN IDO" value={createForm.title} onChange={e => setCreateForm({ ...createForm, title: e.target.value })} disabled={submitting} />
                            </div>
                            <div className="field">
                                <label>Description</label>
                                <input placeholder="Brief description (optional)" value={createForm.description} onChange={e => setCreateForm({ ...createForm, description: e.target.value })} disabled={submitting} />
                            </div>
                            <div className="field">
                                <label>🏷️ Auction Template</label>
                                <select
                                    value={createForm.template || '0'}
                                    onChange={e => {
                                        const template = e.target.value;
                                        setCreateForm({ ...createForm, template });
                                        // Auto-fill based on template
                                        if (template === '1') { // Token Launch
                                            setCreateForm(prev => ({ ...prev, template, duration: '168', description: 'Token Launch - 1 week duration' }));
                                        } else if (template === '2') { // NFT Sale
                                            setCreateForm(prev => ({ ...prev, template, duration: '24', description: 'NFT Collection Sale' }));
                                        } else if (template === '3') { // DAO Treasury
                                            setCreateForm(prev => ({ ...prev, template, duration: '720', description: 'DAO Treasury Token Distribution' }));
                                        }
                                    }}
                                    disabled={submitting}
                                    style={{ background: 'var(--card-bg)', color: 'var(--foreground)' }}
                                >
                                    <option value="0">🎨 Custom Auction</option>
                                    <option value="1">🚀 Token Launch (1 week)</option>
                                    <option value="2">🖼️ NFT Collection Sale (24h)</option>
                                    <option value="3">🏛️ DAO Treasury (1 month)</option>
                                </select>
                            </div>
                            <div className="field-row">
                                <div className="field">
                                    <label>Token Address</label>
                                    <input value={createForm.tokenAddress} onChange={e => setCreateForm({ ...createForm, tokenAddress: e.target.value })} disabled={submitting} />
                                </div>
                                <div className="field">
                                    <label>Symbol</label>
                                    <input placeholder="SMTT" value={createForm.tokenSymbol} onChange={e => setCreateForm({ ...createForm, tokenSymbol: e.target.value })} disabled={submitting} />
                                </div>
                            </div>
                            <div className="field-row">
                                <div className="field">
                                    <label>Total Supply *</label>
                                    <input type="number" placeholder="1000" value={createForm.totalSupply} onChange={e => setCreateForm({ ...createForm, totalSupply: e.target.value })} disabled={submitting} />
                                </div>
                                <div className="field">
                                    <label>Min Price (ETH) *</label>
                                    <input type="number" step="0.001" placeholder="0.01" value={createForm.minPrice} onChange={e => setCreateForm({ ...createForm, minPrice: e.target.value })} disabled={submitting} />
                                </div>
                            </div>
                            <div className="field">
                                <label>Duration</label>
                                <select value={createForm.duration} onChange={e => setCreateForm({ ...createForm, duration: e.target.value })} disabled={submitting}>
                                    <option value="0.0834">5 minutes</option>
                                    <option value="0.5">30 minutes</option>
                                    <option value="1">1 hour</option>
                                    <option value="6">6 hours</option>
                                    <option value="12">12 hours</option>
                                    <option value="24">24 hours</option>
                                    <option value="168">1 week</option>
                                    <option value="720">1 month</option>
                                    <option value="8760">1 year</option>
                                </select>
                            </div>

                            {/* Advanced FHE Settings */}
                            <div className="fhe-settings-section" style={{
                                background: 'var(--primary-bg)',
                                borderRadius: '12px',
                                padding: '16px',
                                marginTop: '16px',
                                border: '1px solid var(--primary)'
                            }}>
                                <h4 style={{
                                    marginBottom: '12px',
                                    color: 'var(--primary)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px'
                                }}>
                                    🔐 Advanced FHE Settings
                                </h4>

                                <div className="field">
                                    <label>🔒 Encrypted Reserve Price (ETH)</label>
                                    <div className="input-suffix">
                                        <input
                                            type="number"
                                            step="0.001"
                                            placeholder="Leave empty for no reserve"
                                            value={createForm.reservePrice}
                                            onChange={e => setCreateForm({ ...createForm, reservePrice: e.target.value })}
                                            disabled={submitting}
                                        />
                                        <span className="suffix-label">ETH</span>
                                    </div>
                                    <div className="field-hint">Hidden minimum - auction cancels if not met</div>
                                </div>

                                <div className="field-row">
                                    <div className="field">
                                        <label>⏱️ Anti-Snipe Protection</label>
                                        <select
                                            value={createForm.antiSnipeMinutes}
                                            onChange={e => setCreateForm({ ...createForm, antiSnipeMinutes: e.target.value })}
                                            disabled={submitting}
                                        >
                                            <option value="0">Disabled</option>
                                            <option value="5">5 min extension</option>
                                            <option value="10">10 min extension</option>
                                            <option value="15">15 min extension</option>
                                        </select>
                                        <div className="field-hint">Extends auction when bid near end</div>
                                    </div>
                                    <div className="field">
                                        <label>🎯 Hidden Trigger (sec)</label>
                                        <input
                                            type="number"
                                            placeholder="300"
                                            value={createForm.antiSnipeThreshold}
                                            onChange={e => setCreateForm({ ...createForm, antiSnipeThreshold: e.target.value })}
                                            disabled={submitting || createForm.antiSnipeMinutes === '0'}
                                        />
                                        <div className="field-hint">Encrypted threshold (only you know)</div>
                                    </div>
                                </div>

                                <div className="bulk-discount-toggle" style={{ marginTop: '12px' }}>
                                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                                        <input
                                            type="checkbox"
                                            checked={createForm.enableBulkDiscount}
                                            onChange={e => setCreateForm({ ...createForm, enableBulkDiscount: e.target.checked })}
                                            disabled={submitting}
                                        />
                                        <span>📦 Enable Hidden Bulk Discounts</span>
                                    </label>
                                </div>

                                {createForm.enableBulkDiscount && (
                                    <div className="field-row" style={{ marginTop: '12px' }}>
                                        <div className="field">
                                            <label>Tier 1: Qty</label>
                                            <input type="number" value={createForm.bulkTier1Qty} onChange={e => setCreateForm({ ...createForm, bulkTier1Qty: e.target.value })} disabled={submitting} />
                                        </div>
                                        <div className="field">
                                            <label>Tier 1: Discount %</label>
                                            <input type="number" value={createForm.bulkTier1Discount} onChange={e => setCreateForm({ ...createForm, bulkTier1Discount: e.target.value })} disabled={submitting} />
                                        </div>
                                        <div className="field">
                                            <label>Tier 2: Qty</label>
                                            <input type="number" value={createForm.bulkTier2Qty} onChange={e => setCreateForm({ ...createForm, bulkTier2Qty: e.target.value })} disabled={submitting} />
                                        </div>
                                        <div className="field">
                                            <label>Tier 2: Discount %</label>
                                            <input type="number" value={createForm.bulkTier2Discount} onChange={e => setCreateForm({ ...createForm, bulkTier2Discount: e.target.value })} disabled={submitting} />
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                        <div className="modal-footer">
                            <button className="btn btn-ghost" onClick={() => setCreateModal(false)} disabled={submitting}>Cancel</button>
                            <button className="btn btn-buy" onClick={handleCreate} disabled={submitting}>
                                {submitting ? <><span className="spin"></span> Creating...</> : '🎯 Create Auction'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Bid Modal */}
            {bidModal && selectedAuction && (
                <div className="modal-overlay" onClick={() => !submitting && setBidModal(false)}>
                    <div className="modal" onClick={e => e.stopPropagation()}>
                        <div className="modal-header">
                            <h3>Place Encrypted Bid</h3>
                            <button className="modal-close" onClick={() => setBidModal(false)}>×</button>
                        </div>
                        <div className="modal-body">
                            {isSeller(selectedAuction) && (
                                <div className="info-box" style={{ borderColor: 'var(--error)', marginBottom: '16px' }}>
                                    <div style={{ color: 'var(--error)', fontWeight: 600 }}>
                                        ⚠️ You cannot bid on your own auction
                                    </div>
                                </div>
                            )}

                            <div className="info-box">
                                <div className="info-row">
                                    <span className="info-label">Auction</span>
                                    <span className="info-value">{selectedAuction.title}</span>
                                </div>
                                <div className="info-row">
                                    <span className="info-label">Token</span>
                                    <span className="info-value">${selectedAuction.tokenSymbol}</span>
                                </div>
                                <div className="info-row">
                                    <span className="info-label">Min Price</span>
                                    <span className="info-value green">{formatEth(selectedAuction.minPriceWei)} ETH</span>
                                </div>
                            </div>

                            <div className="field">
                                <label>Your Max Price (per token)</label>
                                <div className="input-suffix">
                                    <input type="number" step="0.001" placeholder="0.05" value={bidForm.price} onChange={e => setBidForm({ ...bidForm, price: e.target.value })} disabled={submitting || isSeller(selectedAuction)} />
                                    <span className="suffix-label">ETH</span>
                                </div>
                                <div className="field-hint">Price is public (enables price discovery)</div>
                            </div>

                            <div className="field">
                                <label>Quantity (tokens)</label>
                                <input type="number" placeholder="100" value={bidForm.quantity} onChange={e => setBidForm({ ...bidForm, quantity: e.target.value })} disabled={submitting || isSeller(selectedAuction)} />
                                <div className="field-hint">🔒 This quantity is encrypted with FHE</div>
                            </div>

                            {bidForm.price && bidForm.quantity && (
                                <div className="info-box" style={{ borderColor: 'var(--success)' }}>
                                    <div className="info-row">
                                        <span className="info-label">Your Deposit</span>
                                        <span className="info-value green">{(parseFloat(bidForm.price) * parseFloat(bidForm.quantity)).toFixed(4)} ETH</span>
                                    </div>
                                    <div className="field-hint" style={{ marginTop: '8px', fontSize: '0.8rem' }}>
                                        Excess will be refunded if clearing price is lower
                                    </div>
                                </div>
                            )}

                            {/* Autobid (Hidden Max Bid) */}
                            <div className="field" style={{ marginTop: '16px' }}>
                                <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    🤖 Auto-Bid Max (ETH)
                                    <span className="fhe-badge">🔒 ENCRYPTED</span>
                                </label>
                                <div className="input-suffix">
                                    <input
                                        type="number"
                                        step="0.001"
                                        placeholder="Optional - hidden max bid limit"
                                        value={bidForm.maxAutobid}
                                        onChange={e => setBidForm({ ...bidForm, maxAutobid: e.target.value })}
                                        disabled={submitting || isSeller(selectedAuction)}
                                    />
                                    <span className="suffix-label">ETH</span>
                                </div>
                                <div className="field-hint">
                                    System will auto-increment your bid up to this hidden max when outbid
                                </div>
                            </div>
                        </div>
                        <div className="modal-footer">
                            <button className="btn btn-ghost" onClick={() => setBidModal(false)} disabled={submitting}>Cancel</button>
                            <button className="btn btn-buy" onClick={handleBid} disabled={submitting || selectedAuction.status !== 'Active' || isSeller(selectedAuction)}>
                                {submitting ? <><span className="spin"></span> Encrypting...</> : 'Submit Bid'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Confetti celebration */}
            {showConfetti && (
                <div className="confetti">
                    {[...Array(50)].map((_, i) => (
                        <div
                            key={i}
                            className="confetti-piece"
                            style={{
                                left: `${Math.random() * 100}%`,
                                animationDelay: `${Math.random() * 2}s`,
                                backgroundColor: ['#667eea', '#764ba2', '#f093fb', '#f5576c', '#4fd1c7', '#ffd93d'][Math.floor(Math.random() * 6)]
                            }}
                        />
                    ))}
                </div>
            )}

            {/* FHE Encryption Animation Overlay */}
            {encrypting && (
                <div className="encrypting-overlay">
                    <div className="encrypting-content">
                        <div className="encrypting-icon">🔐</div>
                        <div className="encrypting-title">Encrypting with FHE</div>
                        <div className="encrypting-subtitle">
                            Your quantity is being encrypted using Fully Homomorphic Encryption
                        </div>
                        <div className="encrypting-blocks">
                            <div className="encrypt-block"></div>
                            <div className="encrypt-block"></div>
                            <div className="encrypt-block"></div>
                            <div className="encrypt-block"></div>
                            <div className="encrypt-block"></div>
                            <div className="encrypt-block"></div>
                        </div>
                        <div className="encrypting-progress">
                            <div className="encrypting-progress-bar"></div>
                        </div>
                    </div>
                </div>
            )}

            {/* Compare Bids Result Modal */}
            {compareModal && (
                <div className="modal-overlay" onClick={() => setCompareModal(null)}>
                    <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: '600px' }}>
                        <div className="modal-header" style={{ background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.2), rgba(139, 92, 246, 0.1))' }}>
                            <h3>🔒 Encrypted Bid Comparison</h3>
                            <button className="modal-close" onClick={() => setCompareModal(null)}>×</button>
                        </div>
                        <div className="modal-body">
                            {/* Success Badge */}
                            <div style={{ textAlign: 'center', padding: '16px', marginBottom: '20px', background: 'var(--success-bg)', borderRadius: '12px', border: '1px solid var(--success)' }}>
                                <span style={{ fontSize: '2rem' }}>✅</span>
                                <div style={{ color: 'var(--success)', fontWeight: 'bold', marginTop: '8px' }}>FHE Comparison Complete!</div>
                                <div style={{ color: 'var(--gray-400)', fontSize: '0.8rem', marginTop: '4px' }}>
                                    Quantities compared using <code style={{ color: 'var(--primary)' }}>FHE.gt()</code> on-chain
                                </div>
                            </div>

                            {/* Bid Comparison Cards */}
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '20px' }}>
                                {/* Bid 1 */}
                                <div style={{ padding: '16px', background: 'var(--card-bg)', borderRadius: '12px', border: '1px solid var(--primary)' }}>
                                    <div style={{ fontSize: '0.75rem', color: 'var(--primary)', marginBottom: '8px' }}>BID #{compareModal.bid1.id}</div>
                                    <div style={{ fontWeight: 'bold', marginBottom: '12px' }}>{compareModal.bid1.auctionTitle || `Auction ${compareModal.bid1.auctionId}`}</div>

                                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                                        <span style={{ color: 'var(--gray-400)', fontSize: '0.85rem' }}>Deposited:</span>
                                        <span style={{ fontWeight: 'bold', color: 'var(--success)' }}>{formatEth(compareModal.bid1.depositedWei)} ETH</span>
                                    </div>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                                        <span style={{ color: 'var(--gray-400)', fontSize: '0.85rem' }}>Price/Token:</span>
                                        <span>{formatEth(compareModal.bid1.priceWei)} ETH</span>
                                    </div>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px', background: 'rgba(99,102,241,0.1)', borderRadius: '8px' }}>
                                        <span style={{ color: 'var(--gray-400)', fontSize: '0.85rem' }}>Quantity:</span>
                                        <span style={{ color: 'var(--primary)', fontWeight: 'bold' }}>🔐 Encrypted</span>
                                    </div>
                                </div>

                                {/* Bid 2 */}
                                <div style={{ padding: '16px', background: 'var(--card-bg)', borderRadius: '12px', border: '1px solid var(--border)' }}>
                                    <div style={{ fontSize: '0.75rem', color: 'var(--gray-400)', marginBottom: '8px' }}>BID #{compareModal.bid2.id}</div>
                                    <div style={{ fontWeight: 'bold', marginBottom: '12px' }}>{compareModal.bid2.auctionTitle || `Auction ${compareModal.bid2.auctionId}`}</div>

                                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                                        <span style={{ color: 'var(--gray-400)', fontSize: '0.85rem' }}>Deposited:</span>
                                        <span style={{ fontWeight: 'bold', color: 'var(--success)' }}>{formatEth(compareModal.bid2.depositedWei)} ETH</span>
                                    </div>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                                        <span style={{ color: 'var(--gray-400)', fontSize: '0.85rem' }}>Price/Token:</span>
                                        <span>{formatEth(compareModal.bid2.priceWei)} ETH</span>
                                    </div>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px', background: 'rgba(99,102,241,0.1)', borderRadius: '8px' }}>
                                        <span style={{ color: 'var(--gray-400)', fontSize: '0.85rem' }}>Quantity:</span>
                                        <span style={{ color: 'var(--primary)', fontWeight: 'bold' }}>🔐 Encrypted</span>
                                    </div>
                                </div>
                            </div>

                            {/* Conclusion Box - Estimated based on deposits */}
                            <div style={{ padding: '16px', marginTop: '16px', background: 'var(--card-bg)', borderRadius: '12px', border: '2px solid var(--warning)' }}>
                                <h4 style={{ margin: '0 0 12px 0', fontSize: '0.9rem', color: 'var(--warning)' }}>📊 Estimated Comparison</h4>
                                {(() => {
                                    const qty1 = compareModal.bid1.priceWei > 0n ? compareModal.bid1.depositedWei / compareModal.bid1.priceWei : 0n;
                                    const qty2 = compareModal.bid2.priceWei > 0n ? compareModal.bid2.depositedWei / compareModal.bid2.priceWei : 0n;
                                    const isLarger = qty1 > qty2;
                                    const isEqual = qty1 === qty2;
                                    return (
                                        <div style={{ textAlign: 'center' }}>
                                            {isEqual ? (
                                                <div style={{ fontSize: '1.1rem', fontWeight: 'bold', color: 'var(--gray-300)' }}>
                                                    🔄 Bids appear <span style={{ color: 'var(--warning)' }}>EQUAL</span>
                                                </div>
                                            ) : (
                                                <div style={{ fontSize: '1.1rem', fontWeight: 'bold', color: 'var(--gray-300)' }}>
                                                    <span style={{ color: 'var(--primary)' }}>Bid #{isLarger ? compareModal.bid1.id : compareModal.bid2.id}</span>
                                                    {' '}appears to have <span style={{ color: 'var(--success)' }}>MORE TOKENS</span>
                                                </div>
                                            )}
                                            <div style={{ fontSize: '0.75rem', color: 'var(--gray-500)', marginTop: '8px' }}>
                                                Estimated: Bid #{compareModal.bid1.id} ≈ {qty1.toString()} tokens • Bid #{compareModal.bid2.id} ≈ {qty2.toString()} tokens
                                            </div>
                                            <div style={{ fontSize: '0.7rem', color: 'var(--gray-600)', marginTop: '4px', fontStyle: 'italic' }}>
                                                ⚠️ This is an estimate based on deposit/price ratio. Actual encrypted quantity may differ.
                                            </div>
                                        </div>
                                    );
                                })()}
                            </div>

                            {/* Decrypt FHE Result Button */}
                            <div style={{ marginTop: '16px' }}>
                                <button
                                    className="btn btn-buy"
                                    style={{ width: '100%', padding: '14px', fontSize: '1rem' }}
                                    disabled={compareModal.decrypting || compareModal.decryptResult}
                                    onClick={async () => {
                                        if (!compareModal.resultKey) {
                                            notify('No result key found - try comparing again', 'error');
                                            return;
                                        }

                                        try {
                                            setCompareModal(prev => ({ ...prev, decrypting: true }));
                                            notify('🔓 Requesting FHE Gateway decryption...', 'warning');

                                            // Step 1: Get the ebool handle from contract using resultKey
                                            const eboolHandle = await contract.getComparisonResult(compareModal.resultKey);
                                            console.log('ebool Handle from contract:', eboolHandle);

                                            if (!eboolHandle || eboolHandle === '0x0000000000000000000000000000000000000000000000000000000000000000') {
                                                throw new Error('No comparison result found');
                                            }

                                            // Step 2: Use Gateway to decrypt the ebool
                                            const { publicDecrypt } = await import('./utils/fhevmInstance');
                                            const result = await publicDecrypt([eboolHandle]);

                                            console.log('Gateway Decryption Result:', result);

                                            // Step 3: Check if bid1 > bid2
                                            const clearValue = result.clearValues?.[eboolHandle];
                                            const isLarger = clearValue === true || clearValue === 1n || clearValue === 1 || clearValue === '1';

                                            // Update modal with decryption result
                                            setCompareModal(prev => ({
                                                ...prev,
                                                decrypting: false,
                                                decryptResult: {
                                                    success: true,
                                                    winner: isLarger ? prev.bid1.id : prev.bid2.id,
                                                    isLarger: isLarger,
                                                    rawValue: clearValue
                                                }
                                            }));
                                            notify(`🎉 FHE Decryption complete!`, 'success');
                                        } catch (err) {
                                            console.error('FHE Decryption failed:', err);
                                            setCompareModal(prev => ({
                                                ...prev,
                                                decrypting: false,
                                                decryptResult: {
                                                    success: false,
                                                    error: err.message
                                                }
                                            }));
                                            notify('❌ Gateway decryption failed - see modal for details', 'error');
                                        }
                                    }}
                                >
                                    {compareModal.decrypting ? '⏳ Decrypting...' :
                                        compareModal.decryptResult ? '✅ Decrypted!' :
                                            '🔓 Decrypt FHE Result (via ZAMA Gateway)'}
                                </button>
                                <div style={{ fontSize: '0.7rem', color: 'var(--gray-500)', textAlign: 'center', marginTop: '6px' }}>
                                    {compareModal.resultKey ?
                                        `Result Key: ${compareModal.resultKey.slice(0, 10)}...${compareModal.resultKey.slice(-8)}` :
                                        'Comparison in progress...'}
                                </div>
                            </div>

                            {/* FHE Decryption Result Display */}
                            {compareModal.decryptResult && (
                                <div style={{
                                    marginTop: '16px',
                                    padding: '20px',
                                    background: compareModal.decryptResult.success
                                        ? 'linear-gradient(135deg, rgba(34, 197, 94, 0.2), rgba(16, 185, 129, 0.1))'
                                        : 'linear-gradient(135deg, rgba(239, 68, 68, 0.2), rgba(220, 38, 38, 0.1))',
                                    borderRadius: '12px',
                                    border: `2px solid ${compareModal.decryptResult.success ? 'var(--success)' : 'var(--error)'}`,
                                    textAlign: 'center'
                                }}>
                                    {compareModal.decryptResult.success ? (
                                        <>
                                            <div style={{ fontSize: '2rem', marginBottom: '8px' }}>🎉</div>
                                            <h4 style={{ margin: '0 0 8px 0', color: 'var(--success)', fontSize: '1.1rem' }}>
                                                FHE DECRYPTION RESULT
                                            </h4>
                                            <div style={{ fontSize: '1.3rem', fontWeight: 'bold', color: 'white', marginBottom: '8px' }}>
                                                Bid #{compareModal.decryptResult.winner} has{' '}
                                                <span style={{ color: 'var(--success)' }}>MORE TOKENS!</span>
                                            </div>
                                            <div style={{ fontSize: '0.8rem', color: 'var(--gray-400)', marginTop: '12px' }}>
                                                ✅ Decrypted via ZAMA Gateway • Raw ebool: {String(compareModal.decryptResult.rawValue)}
                                            </div>
                                        </>
                                    ) : (
                                        <>
                                            <div style={{ fontSize: '2rem', marginBottom: '8px' }}>❌</div>
                                            <h4 style={{ margin: '0 0 8px 0', color: 'var(--error)', fontSize: '1.1rem' }}>
                                                Gateway Decryption Failed
                                            </h4>
                                            <div style={{ fontSize: '0.85rem', color: 'var(--gray-400)' }}>
                                                {compareModal.decryptResult.error || 'Unknown error'}
                                            </div>
                                            <div style={{ fontSize: '0.75rem', color: 'var(--gray-500)', marginTop: '8px' }}>
                                                The estimation above can be used as reference
                                            </div>
                                        </>
                                    )}
                                </div>
                            )}

                            {/* FHE Explanation */}
                            <div style={{ padding: '16px', marginTop: '16px', background: 'linear-gradient(135deg, rgba(99,102,241,0.1), rgba(139,92,246,0.05))', borderRadius: '12px', border: '1px solid rgba(99,102,241,0.3)' }}>
                                <h4 style={{ margin: '0 0 12px 0', fontSize: '0.9rem' }}>🔐 How This Works</h4>
                                <p style={{ margin: 0, color: 'var(--gray-400)', fontSize: '0.8rem', lineHeight: 1.6 }}>
                                    The contract compared your encrypted bid quantities using <code style={{ color: 'var(--primary)' }}>FHE.gt(bid1.qty, bid2.qty)</code>.
                                    The result is an <strong>encrypted boolean (ebool)</strong> - even the blockchain doesn't know which bid is larger!
                                    Only you can decrypt the result with your private key via the Gateway.
                                </p>
                            </div>

                            {/* Transaction Hash */}
                            {compareModal.txHash && (
                                <div style={{ marginTop: '16px', fontSize: '0.75rem', color: 'var(--gray-500)', textAlign: 'center' }}>
                                    Tx: <a href={`https://sepolia.etherscan.io/tx/${compareModal.txHash}`} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--primary)' }}>
                                        {compareModal.txHash.slice(0, 10)}...{compareModal.txHash.slice(-8)}
                                    </a>
                                </div>
                            )}
                        </div>
                        <div className="modal-footer">
                            <button className="btn btn-buy" onClick={() => setCompareModal(null)} style={{ width: '100%' }}>
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Toast */}
            {toast && <div className={`toast ${toast.type}`}>{toast.msg}</div>}

            <div className="footer">
                Powered by <a href="https://www.zama.ai/" target="_blank" rel="noopener noreferrer">Zama FHEVM</a> • Confidential Dutch Auctions
            </div>
        </div>
    );
}

export default App;
